import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  TrendingDown, TrendingUp, CalendarDays, BarChart3, Settings, 
  LogOut, Menu, X, User as UserIcon, Sun, Moon 
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const TopbarItem: React.FC<{ to: string; icon: React.ReactNode; label: string; onClick?: () => void }> = ({ to, icon, label, onClick }) => {
  return (
    <NavLink 
      to={to} 
      onClick={onClick}
      className={({ isActive }) => 
        `flex items-center gap-2 px-3.5 lg:px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 select-none ${
          isActive 
            ? 'bg-[#7c3aed] text-white shadow-md shadow-purple-950/40 scale-[1.02]' 
            : 'text-purple-200/90 hover:text-white hover:bg-white/10'
        }`
      }
    >
      <span className="shrink-0">{icon}</span>
      <span className="tracking-wider">{label}</span>
    </NavLink>
  );
};

const MobileSidebarItem: React.FC<{ to: string; icon: React.ReactNode; label: string; onClick?: () => void }> = ({ to, icon, label, onClick }) => {
  return (
    <NavLink 
      to={to} 
      onClick={onClick}
      className={({ isActive }) => 
        `flex items-center gap-3 px-4 py-2.5 rounded-full transition-all duration-200 font-bold text-xs uppercase tracking-wider ${
          isActive 
            ? 'bg-[#7c3aed] text-white shadow-md shadow-purple-950/40' 
            : 'text-purple-200/90 hover:bg-white/10 hover:text-white'
        }`
      }
    >
      {icon}
      <span>{label}</span>
    </NavLink>
  );
};

export const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    // Default to light mode if no theme is saved
    const savedTheme = localStorage.getItem('theme');
    const isDark = savedTheme === 'dark';
    setIsDarkMode(isDark);
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    if (newMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const pageTitle = () => {
    switch (location.pathname) {
      case '/dashboard': return 'Despesas';
      case '/revenues': return 'Receitas';
      case '/agenda': return 'Agenda';
      case '/reports': return 'Relatórios';
      case '/profile': return 'Perfil';
      case '/settings': return 'Configurações';
      default: return localStorage.getItem('app_header_text') || 'Gestão Fácil';
    }
  };

  const headerText = localStorage.getItem('app_header_text') || 'Gestão Fácil';
  const savedSubtitle = localStorage.getItem('app_header_subtitle');
  const headerSubtitle = savedSubtitle && savedSubtitle !== 'Painel Web' ? savedSubtitle : 'By Alonedev.com';
  const logoUrl = localStorage.getItem('report_logo_url');

  return (
    <div className="flex flex-col h-screen print:h-auto bg-slate-50 dark:bg-slate-950/50 overflow-hidden print:overflow-visible font-sans transition-colors duration-300">
      
      {/* Top Navigation Bar - Matching Screenshot */}
      <header className="h-16 bg-gradient-to-r from-[#4c1d95] via-[#5b21b6] to-[#3b0764] dark:from-[#18092e] dark:via-[#1e1035] dark:to-[#0f0728] border-b border-purple-500/20 shadow-md shadow-purple-950/30 backdrop-blur-md grid grid-cols-[1fr_auto_1fr] items-center px-4 lg:px-8 z-30 sticky top-0 transition-colors duration-300 print:hidden">
        
        {/* Left: Brand Logo & Title */}
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setIsMobileMenuOpen(true)} 
            className="md:hidden text-white/90 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            title="Abrir Menu"
          >
            <Menu size={22} />
          </button>
          
          <div 
            className="flex items-center gap-3 cursor-pointer group select-none"
            onClick={() => navigate('/dashboard')}
          >
            {logoUrl ? (
              <div className="w-9 h-9 rounded-xl flex items-center justify-center overflow-hidden bg-white/10 border border-white/20 p-1 group-hover:scale-105 transition-transform duration-200">
                <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-600 shadow-md shadow-indigo-600/30 border border-white/25 group-hover:scale-105 transition-transform duration-200">
                <TrendingUp size={20} className="text-white drop-shadow-sm" />
              </div>
            )}
            
            <div className="flex flex-col text-left">
              <div className="flex items-center gap-1.5">
                <h1 className="text-white font-extrabold text-base tracking-tight leading-none">
                  {headerText}
                </h1>
                <span className="bg-white/20 text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded-md uppercase tracking-wider border border-white/15 shadow-sm">
                  ERP
                </span>
              </div>
              <span className="text-purple-200/75 text-[11px] font-medium tracking-wide mt-0.5">
                {headerSubtitle}
              </span>
            </div>
          </div>
        </div>

        {/* Center: Navigation Pill Dock */}
        <nav className="hidden md:flex items-center bg-[#25084d]/75 dark:bg-black/45 backdrop-blur-md p-1 rounded-full border border-white/10 shadow-inner">
          <TopbarItem to="/dashboard" icon={<TrendingDown size={15} />} label="Despesas" />
          <TopbarItem to="/revenues" icon={<TrendingUp size={15} />} label="Receitas" />
          <TopbarItem to="/agenda" icon={<CalendarDays size={15} />} label="Agenda" />
          <TopbarItem to="/reports" icon={<BarChart3 size={15} />} label="Relatórios" />
          <TopbarItem to="/settings" icon={<Settings size={15} />} label="Configurações" />
        </nav>

        {/* Right: Theme Toggle, User Profile & Logout */}
        <div className="flex items-center gap-2 sm:gap-3 justify-end">
          {/* Dark Mode Toggle */}
          <button 
            onClick={toggleDarkMode} 
            className="p-2 text-purple-200 hover:text-white hover:bg-white/10 rounded-full transition-all duration-200 active:scale-95"
            title={isDarkMode ? "Mudar para modo claro" : "Mudar para modo escuro"}
          >
            {isDarkMode ? <Sun size={19} /> : <Moon size={19} />}
          </button>

          {/* User Profile Pill */}
          <NavLink 
            to="/profile" 
            className="flex items-center gap-2.5 px-3 py-1 rounded-full bg-white/10 dark:bg-white/5 border border-white/10 hover:bg-white/15 hover:border-white/20 transition-all duration-200 group select-none shadow-sm"
            title="Ver meu perfil"
          >
            <div className="w-7 h-7 rounded-full overflow-hidden border border-white/30 bg-purple-900 flex items-center justify-center shrink-0 shadow-sm group-hover:scale-105 transition-transform duration-200">
              {user?.photoUrl ? (
                <img src={user.photoUrl} alt="User" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-tr from-cyan-400 via-pink-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold">
                  {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </div>
              )}
            </div>
            <div className="hidden sm:flex flex-col text-left leading-none">
              <span className="text-white font-bold text-xs truncate max-w-[120px]">
                {user?.name || 'Super Administrador'}
              </span>
              <span className="text-[9px] font-extrabold uppercase text-purple-300 tracking-wider mt-0.5">
                {user?.role === 'admin' ? 'ADMIN' : (user?.role?.toUpperCase() || 'COLABORADOR')}
              </span>
            </div>
          </NavLink>

          {/* Logout Button */}
          <button 
            onClick={handleLogout} 
            className="p-2 text-purple-200 hover:text-white hover:bg-white/10 rounded-full transition-all duration-200 active:scale-95 hover:rotate-6"
            title="Sair do sistema"
          >
            <LogOut size={19} />
          </button>
        </div>
      </header>

      {/* Mobile Sidebar (Overlay) */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-50 md:hidden flex">
            <motion.div 
              initial={{ x: -280, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -280, opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              className="w-72 bg-gradient-to-b from-[#4c1d95] to-[#2e1065] dark:from-[#18092e] dark:to-[#090214] backdrop-blur-2xl h-full shadow-2xl flex flex-col border-r border-purple-500/20"
            >
              <div className="h-16 flex items-center justify-between px-5 border-b border-purple-500/20">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-blue-500 to-purple-600 shadow-md border border-white/20">
                    <TrendingUp size={16} className="text-white" />
                  </div>
                  <span className="text-white font-extrabold text-sm">{headerText}</span>
                </div>
                <button 
                  onClick={() => setIsMobileMenuOpen(false)} 
                  className="text-purple-200 hover:text-white p-1 rounded-lg hover:bg-white/10"
                >
                  <X size={20}/>
                </button>
              </div>

              <div className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
                <div className="mb-3">
                  <p className="px-4 text-[10px] font-bold text-purple-300 dark:text-slate-400 uppercase tracking-widest mb-2">Financeiro</p>
                  <div className="space-y-1">
                    <MobileSidebarItem to="/dashboard" icon={<TrendingDown size={17} />} label="Despesas" onClick={() => setIsMobileMenuOpen(false)}/>
                    <MobileSidebarItem to="/revenues" icon={<TrendingUp size={17} />} label="Receitas" onClick={() => setIsMobileMenuOpen(false)}/>
                    <MobileSidebarItem to="/agenda" icon={<CalendarDays size={17} />} label="Agenda" onClick={() => setIsMobileMenuOpen(false)}/>
                    <MobileSidebarItem to="/reports" icon={<BarChart3 size={17} />} label="Relatórios" onClick={() => setIsMobileMenuOpen(false)}/>
                  </div>
                </div>

                <div className="pt-2 border-t border-purple-500/20">
                  <p className="px-4 text-[10px] font-bold text-purple-300 dark:text-slate-400 uppercase tracking-widest mb-2">Gerenciamento</p>
                  <div className="space-y-1">
                    <MobileSidebarItem to="/settings" icon={<Settings size={17} />} label="Configurações" onClick={() => setIsMobileMenuOpen(false)}/>
                    <MobileSidebarItem to="/profile" icon={<UserIcon size={17} />} label="Meu Perfil" onClick={() => setIsMobileMenuOpen(false)}/>
                  </div>
                </div>
              </div>

              <div className="p-4 border-t border-purple-500/20">
                <button 
                  onClick={handleLogout} 
                  className="flex items-center gap-2 text-purple-100 hover:text-white text-xs font-bold uppercase tracking-wider w-full p-2.5 rounded-full hover:bg-white/10 transition-colors justify-center"
                >
                  <LogOut size={16} /> Sair
                </button>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex-1 bg-black/50 backdrop-blur-sm" 
              onClick={() => setIsMobileMenuOpen(false)}
            />
          </div>
        )}
      </AnimatePresence>

      {/* Main Content Area with Page Transitions */}
      <main className="flex-1 overflow-auto print:overflow-visible p-4 lg:p-6 relative">
        {/* Page Title for Mobile */}
        <div className="md:hidden mb-4">
          <h2 className="text-xl font-bold text-slate-800 dark:text-white tracking-tight">{pageTitle()}</h2>
        </div>
        
        <div className="max-w-7xl mx-auto min-h-full flex flex-col relative z-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              className="w-full flex-1 flex flex-col"
            >
              {children}
            </motion.div>
          </AnimatePresence>

          {/* Footer Signature */}
          <footer className="mt-8 pb-4 pt-2 text-center text-xs text-slate-400 dark:text-slate-500 print:hidden select-none">
            <p className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2">
              <span>2026 Gestão Fácil ERP ®</span>
              <span className="text-slate-300 dark:text-slate-600">•</span>
              <span>Todos os direitos reservados.</span>
              <span className="text-slate-300 dark:text-slate-600">•</span>
              <span>
                Desenvolvido por{' '}
                <a 
                  href="http://alonedev.com/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="font-semibold text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:underline transition-colors"
                >
                  AloneDev.com ®
                </a>
              </span>
              <span className="text-slate-300 dark:text-slate-600">•</span>
              <span>Todos os direitos reservados.</span>
            </p>
          </footer>
        </div>
        
        {/* Decorative Background Blur Elements */}
        <div className="fixed top-20 left-10 w-72 h-72 bg-purple-500/10 dark:bg-purple-600/10 rounded-full blur-3xl pointer-events-none -z-10"></div>
        <div className="fixed bottom-20 right-10 w-80 h-80 bg-blue-500/10 dark:bg-indigo-600/10 rounded-full blur-3xl pointer-events-none -z-10"></div>
      </main>
    </div>
  );
};
